import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import electromob_logo from "../Assets/electromob_logo.png";
import { FaFacebookSquare, FaLinkedin, FaInstagramSquare } from "react-icons/fa";
import { FaSquareXTwitter } from "react-icons/fa6";

/* ─── social links ───────────────────────────────────────────────────────── */
const socials = [
  {
    Icon: FaFacebookSquare,
    label: "Facebook",
    href: "https://facebook.com/electromob",
  },
  {
    Icon: FaLinkedin,
    label: "LinkedIn",
    href: "https://linkedin.com/company/electromob",
  },
  {
    Icon: FaInstagramSquare,
    label: "Instagram",
    href: "https://instagram.com/electromob",
  },
  {
    Icon: FaSquareXTwitter,
    label: "X",
    href: "https://x.com/electromob",
  },
];

/* ═══════════════════════════════════════════════════════════════════════════ */
function Navbar({ isScrolled }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  /* track mobile breakpoint */
  useEffect(() => {
    const update = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setIsMenuOpen(false);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  /* lock body scroll when mobile menu open */
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  /* ── navbar background logic ──────────────────────────────────────────
     mobile  → always solid [#102a05]
     desktop → transparent until scrolled, then solid + blur
  ─────────────────────────────────────────────────────────────────────── */
  const navBg = isMobile
    ? "bg-[#102a05]"
    : isScrolled
    ? "bg-[#102a05]/90 backdrop-blur-md shadow-md"
    : "bg-transparent";

  return (
    <nav
      className={`fixed w-full h-[70px] z-40 transition-all duration-300 ${navBg}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[70px]">

          {/* logo */}
          <Link to="/" className="flex items-center">
            <img
              src={electromob_logo}
              className="w-20"
              alt="Electromob logo"
            />
          </Link>

          {/* desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            <NavLink to="/"         label="Home"     />
            <NavLink to="/Aboutusp" label="About"    />
            <NavLink to="/profiles" label="Team"     />
            <NavLink to="/Products" label="Products" />
            <NavLink to="/contact"  label="Contact"  />
            <Link
              to="/Quotep"
              className="ml-4 bg-white text-[#4a8f0f] font-semibold text-sm
                         px-5 py-2.5 rounded-md hover:bg-white/90
                         transition-colors duration-200"
            >
              Get a quote
            </Link>
          </div>

          {/* hamburger — always visible on mobile */}
          <button
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            className="md:hidden inline-flex z-50 items-center justify-center
                       p-2 rounded-md text-white hover:bg-white/10
                       transition-colors duration-200 focus:outline-none
                       focus:ring-2 focus:ring-white/60"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* ── backdrop ──────────────────────────────────────────────────── */}
      {isMenuOpen && (
        <div
          onClick={toggleMenu}
          className="md:hidden fixed inset-0 bg-black/50 z-30"
        />
      )}

      {/* ── mobile drawer ─────────────────────────────────────────────── */}
      <div
        className={`md:hidden fixed top-0 right-0 bottom-0 w-[260px]
                    bg-[#102a05] text-white shadow-2xl z-40
                    flex flex-col justify-between
                    transform transition-transform duration-300 ease-in-out
                    ${isMenuOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* top — logo + close button */}
        <div>
          <div className="flex items-center justify-between px-5 h-[70px]
                          border-b border-white/10">
            <img
              src={electromob_logo}
              className="w-16"
              alt="Electromob logo"
            />
            <button
              onClick={toggleMenu}
              aria-label="Close menu"
              className="text-white p-1.5 rounded-md hover:bg-white/10
                         transition-colors duration-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* nav links */}
          <div className="px-4 pt-4 flex flex-col gap-1">
            <MobileNavLink to="/"         label="Home"     onClick={toggleMenu} />
            <MobileNavLink to="/Aboutusp" label="About"    onClick={toggleMenu} />
            <MobileNavLink to="/profiles" label="Team"     onClick={toggleMenu} />
            <MobileNavLink to="/Products" label="Products" onClick={toggleMenu} />
            <MobileNavLink to="/contact"  label="Contact"  onClick={toggleMenu} />

            {/* get a quote CTA inside drawer */}
            <Link
              to="/Quotep"
              onClick={toggleMenu}
              className="mt-3 flex items-center justify-center bg-[#6ab615]
                         hover:bg-[#5a9b12] text-white font-semibold text-sm
                         px-5 py-3 rounded-xl transition-colors duration-200"
            >
              Get a quote
            </Link>
          </div>
        </div>

        {/* bottom — social icons */}
        <div className="px-5 mb-10">
          <div className="border-t border-white/10 pt-6">
            <p className="text-white/50 text-xs uppercase tracking-widest mb-3">
              Follow us
            </p>
            <ul className="flex flex-row gap-3">
              {socials.map(({ Icon, label, href }) => (
                <li key={label}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Electromob on ${label}`}
                    className="flex items-center justify-center bg-white/10
                               hover:bg-[#6ab615] transition-colors duration-200
                               p-2.5 rounded-lg"
                  >
                    <Icon className="text-lg" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
}

/* ─── desktop nav link ───────────────────────────────────────────────────── */
function NavLink({ to, label }) {
  return (
    <Link
      to={to}
      className="relative px-3 py-2 text-sm font-medium text-white/90
                 hover:text-white transition-colors duration-200 group"
    >
      {label}
      <span
        className="absolute left-3 right-3 -bottom-0.5 h-[2px] bg-white
                   scale-x-0 group-hover:scale-x-100 origin-left
                   transition-transform duration-200"
      />
    </Link>
  );
}

/* ─── mobile nav link ────────────────────────────────────────────────────── */
function MobileNavLink({ to, label, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center px-4 py-3.5 rounded-xl text-sm font-medium
                 text-white/90 hover:text-white hover:bg-white/10
                 transition-all duration-200"
    >
      {label}
    </Link>
  );
}

export default Navbar;